import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress as ProgressBar } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Target, Trash, House, CalendarBlank, ChartLineUp, Medal } from '@phosphor-icons/react';

const GOAL_TYPES = [
  { id: 'daily_minutes', label: 'Daily Cardio Minutes', unit: 'min/day' },
  { id: 'weekly_minutes', label: 'Weekly Cardio Minutes', unit: 'min/week' },
  { id: 'weight_loss', label: 'Weight Loss', unit: 'lbs' },
  { id: 'total_sessions', label: 'Total Sessions', unit: 'sessions' },
  { id: 'streak_days', label: 'Streak Days', unit: 'days' },
];

const Goals = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newGoal, setNewGoal] = useState({ goal_type: '', target_value: '' });

  useEffect(() => {
    fetchGoals();
  }, [token]);

  const fetchGoals = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/goals`, { withCredentials: true, headers });
      setGoals(response.data);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = async () => {
    if (!newGoal.goal_type || !newGoal.target_value) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/goals`, { goal_type: newGoal.goal_type, target_value: parseFloat(newGoal.target_value) }, { withCredentials: true, headers });
      toast.success('Goal created!');
      setNewGoal({ goal_type: '', target_value: '' });
      setDialogOpen(false);
      fetchGoals();
    } catch (error) {
      toast.error('Failed to create goal');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/goals/${goalId}`, { withCredentials: true, headers });
      toast.success('Goal deleted');
      fetchGoals();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const getGoalLabel = (type) => GOAL_TYPES.find(g => g.id === type)?.label || type;
  const getGoalUnit = (type) => GOAL_TYPES.find(g => g.id === type)?.unit || '';

  if (loading) {
    return (<div className="min-h-screen bg-background flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>);
  }

  return (
    <div className="min-h-screen bg-background pb-24 hexagon-bg" data-testid="goals-screen">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-full bg-card flex items-center justify-center" data-testid="back-btn"><ArrowLeft weight="bold" className="w-5 h-5 text-foreground" /></button>
          <div className="flex-1"><h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Goals</h1><p className="text-xs text-muted-foreground">Set your targets</p></div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#D4AF37] text-black rounded-full h-10 px-4" data-testid="add-goal-btn"><Plus weight="bold" className="w-5 h-5" /></Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader><DialogTitle className="text-foreground">Create New Goal</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-muted-foreground">Goal Type</Label>
                  <Select value={newGoal.goal_type} onValueChange={(v) => setNewGoal({ ...newGoal, goal_type: v })}>
                    <SelectTrigger className="mt-2 bg-background" data-testid="goal-type-select"><SelectValue placeholder="Select goal type" /></SelectTrigger>
                    <SelectContent>{GOAL_TYPES.map((type) => (<SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-muted-foreground">Target Value</Label>
                  <Input type="number" value={newGoal.target_value} onChange={(e) => setNewGoal({ ...newGoal, target_value: e.target.value })} placeholder="e.g., 30" className="mt-2 bg-background" data-testid="target-value-input" />
                </div>
                <Button onClick={handleAddGoal} className="w-full bg-[#D4AF37] text-black rounded-full" data-testid="save-goal-btn">Create Goal</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-6 space-y-4">
        {goals.length === 0 ? (
          <div className="premium-card p-8 text-center">
            <Target weight="duotone" className="w-16 h-16 text-[#D4AF37]/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Goals Yet</h3>
            <p className="text-muted-foreground mb-4">Set your first goal to start tracking your progress</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#D4AF37] text-black rounded-full" data-testid="create-first-goal-btn"><Plus weight="bold" className="w-5 h-5 mr-2" />Create Goal</Button>
          </div>
        ) : (
          goals.map((goal) => {
            const progress = Math.min(100, Math.round((goal.current_value / goal.target_value) * 100));
            return (
              <div key={goal.goal_id} className="premium-card p-5" data-testid={`goal-card-${goal.goal_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-foreground font-semibold">{getGoalLabel(goal.goal_type)}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{goal.current_value} / {goal.target_value} {getGoalUnit(goal.goal_type)}</p>
                  </div>
                  <button onClick={() => handleDeleteGoal(goal.goal_id)} className="text-muted-foreground hover:text-red-500 transition-colors" data-testid={`delete-goal-${goal.goal_id}`}><Trash weight="duotone" className="w-5 h-5" /></button>
                </div>
                <ProgressBar value={progress} className="h-2" />
                <div className="flex justify-between mt-2">
                  <span className={`text-xs font-semibold ${goal.completed ? 'text-green-500' : 'text-[#D4AF37]'}`}>{progress}%</span>
                  {goal.completed && <span className="text-xs text-green-500 font-semibold">Completed!</span>}
                </div>
              </div>
            );
          })
        )}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 bg-background/90 backdrop-blur-xl border-t border-border px-6 py-3 z-50">
        <div className="max-w-lg mx-auto flex items-center justify-around">
          <Link to="/dashboard" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-home"><House weight="duotone" className="w-6 h-6" /><span className="text-xs">Home</span></Link>
          <Link to="/calendar" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-calendar"><CalendarBlank weight="duotone" className="w-6 h-6" /><span className="text-xs">Calendar</span></Link>
          <Link to="/progress" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-progress"><ChartLineUp weight="duotone" className="w-6 h-6" /><span className="text-xs">Progress</span></Link>
          <Link to="/goals" className="flex flex-col items-center gap-1 text-[#D4AF37]" data-testid="nav-goals"><Target weight="fill" className="w-6 h-6" /><span className="text-xs">Goals</span></Link>
          <Link to="/milestones" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-milestones"><Medal weight="duotone" className="w-6 h-6" /><span className="text-xs">Badges</span></Link>
        </div>
      </nav>
    </div>
  );
};

export default Goals;
